'use client'

import { useEffect, useRef, useState, useTransition } from 'react'

import { LogOut } from 'lucide-react'

import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { AuthModal } from './auth-modal'

type AuthButtonProps = {
  isLoggedIn: boolean
  logoutAction: () => Promise<void>
  user?: any
}

export function ClientAuthButton({ isLoggedIn, logoutAction, user }: AuthButtonProps) {
  const [isPending, startTransition] = useTransition()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (isLoggedIn) {
    // Get the user's profile picture if available
    const profilePicture = user?.properties?.picture || user?.picture
    const userName = user?.properties?.name || user?.name || 'Account'
    const userEmail = user?.properties?.email || user?.email
    const firstName = userName.split(' ')[0]

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className={cn(
            'flex items-center gap-2 rounded-md border border-kinetic-outline-variant/40',
            'bg-kinetic-surface-container/60 px-3 py-2 text-kinetic-on-surface transition-colors',
            'hover:bg-kinetic-surface-container hover:border-kinetic-primary-container/40'
          )}
          aria-expanded={showDropdown}
          aria-haspopup="true"
        >
          {profilePicture ? (
            <img
              src={profilePicture}
              alt={userName}
              className="h-6 w-6 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-kinetic-primary-container text-xs font-medium text-kinetic-on-primary">
              {firstName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="hidden font-headline text-xs font-semibold uppercase tracking-[0.12em] sm:inline">
            {firstName}
          </span>
        </button>

        {showDropdown && (
          <div
            className={cn(
              'absolute right-0 z-50 mt-2 w-56 divide-y rounded-md py-1 shadow-lg',
              'divide-kinetic-outline-variant/30 border border-kinetic-outline-variant/40',
              'bg-kinetic-surface-container-highest'
            )}
          >
            <div className="px-4 py-3">
              <p className="text-sm font-medium text-kinetic-on-surface">{userName}</p>
              {userEmail && (
                <p className="truncate text-sm text-kinetic-on-surface-variant">{userEmail}</p>
              )}
            </div>
            <div className="py-1">
              <form
                action={() => {
                  startTransition(async () => {
                    await logoutAction()
                  })
                }}
              >
                <button
                  type="submit"
                  disabled={isPending}
                  className="flex w-full items-center px-4 py-2 text-sm text-kinetic-on-surface-variant transition-colors hover:bg-kinetic-surface-container hover:text-kinetic-on-surface"
                >
                  <LogOut size={16} className="mr-2" />
                  {isPending ? 'Signing out...' : 'Sign out'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          'font-headline text-xs font-semibold uppercase tracking-[0.12em]',
          'text-kinetic-on-surface-variant hover:bg-kinetic-surface-container/40 hover:text-kinetic-on-surface'
        )}
        onClick={() => setShowAuthModal(true)}
      >
        Sign in
      </Button>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  )
}
